/**
 * @fileoverview Agent definition for single-chapter-drafter.
 *
 * This agent drafts a complete short story in a single chapter. It is the
 * short-form counterpart to chapter-drafter-v2 (which writes one chapter of
 * a multi-chapter narrative).
 *
 * Key differences from chapter-drafter-v2:
 * - Frames the task as "write a complete story" not "write a chapter"
 * - No chapter boundary enforcement or early/late chapter heuristics
 * - Pacing guidance calibrated for story-level density
 * - Direct plotThread awareness via plotThreadStatus input
 * - Omits lastThreeChaptersContent, nextChapterContext, seriesContext
 *
 * @category narrative.prose.drafting
 * @type primitive
 */

import type { AppAgentDefinition } from '@/domains/agents/schemas';
import { InputSchema, OutputSchema } from './schema';
import { prompt } from './prompt';

// =============================================================================
// AGENT DEFINITION
// =============================================================================

export const definition: AppAgentDefinition = {
  id: 'single-chapter-drafter',
  kind: 'primitive',
  name: 'Single Chapter Drafter',
  description: 'Drafts a complete short story with a full narrative arc, optimized for single-chapter projects.',
  category: 'writing',
  defaultPrompt: prompt,
  systemPrompt: `You are an expert fiction author who creates compelling short stories with vivid prose, authentic dialogue, and strong pacing. Your writing reflects deep understanding of character motivations and complete narrative arcs. Every paragraph in your stories carries structural weight — you never waste a word.`,
  inputSchema: InputSchema,
  outputSchema: OutputSchema,

  // --- Metadata (Hybrid Architecture) ---
  metadata: {
    status: 'active',
    type: 'primitive',
    addedIn: '2026-03',
    lastUpdated: '2026-03',
    taxonomyPath: 'narrative.prose.drafting',
    usedBy: [
      'use-content-creation-v2.ts',
    ],
    usedInComposites: [
      'chapter-drafter-enhanced-v2',
      'chapter-drafter-comparison-v2',
    ],
    relatedAgents: [
      'chapter-drafter-v2',
      'draft-refiner-v2',
    ],
  },
};
